import { useState, useEffect } from "react";
import { Youtube, Facebook, Linkedin, Twitter, Globe, LucideIcon, Mail, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import BookingDialog from "@/components/BookingDialog";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";

interface SocialLink { platform: string; url: string; }

const platformIcons: Record<string, LucideIcon> = {
  youtube: Youtube, facebook: Facebook, linkedin: Linkedin, twitter: Twitter, website: Globe,
};
const platformLabels: Record<string, string> = {
  youtube: "YouTube", facebook: "Facebook", linkedin: "LinkedIn", twitter: "Twitter/X", website: "Website",
};

const Contact = () => {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const { content } = useWebsiteContent();

  useEffect(() => {
    const fetchSocialLinks = async () => {
      const { data } = await supabase.from("coach_social_links").select("platform, url").eq("is_active", true);
      if (data && data.length > 0) {
        setSocialLinks(data);
      } else {
        setSocialLinks([
          { platform: "youtube", url: "https://www.youtube.com/@gocoachifyai" },
          { platform: "facebook", url: "https://www.facebook.com/gocoachifyai" },
          { platform: "linkedin", url: "https://www.linkedin.com/company/gocoachifyai" },
        ]);
      }
    };
    fetchSocialLinks();
  }, []);

  return (
    <section id="contact" className="py-20 md:py-28 bg-foreground text-background">
      <div className="max-w-[980px] mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl lg:text-[56px] font-semibold text-background tracking-[-0.03em] leading-tight mb-4">
              {content.contact_title}
            </h2>
            <p className="text-xl text-background/50 font-normal max-w-xl mx-auto">
              {content.contact_subtitle}
            </p>
          </div>

          {/* CTA Card */}
          <div className="bg-background/[0.05] rounded-2xl p-8 md:p-12 border border-background/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-xl font-semibold text-background mb-3 tracking-tight">
                  {content.contact_cta_title}
                </h3>
                <p className="text-background/50 text-[15px] mb-8 leading-relaxed">
                  {content.contact_cta_description}
                </p>
                <BookingDialog
                  trigger={
                    <button className="inline-flex items-center justify-center gap-2 font-medium transition-all h-11 px-6 text-sm rounded-full bg-background text-foreground hover:bg-background/90 group w-full sm:w-auto">
                      {content.contact_cta_button}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  }
                />
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="text-background/30 text-[11px] font-semibold uppercase tracking-[0.1em] mb-3">
                    Email
                  </h4>
                  <a href="mailto:hello@gocoachify.com" className="text-background/60 hover:text-background transition-colors text-sm flex items-center gap-2" aria-label="Email us">
                    <Mail className="w-4 h-4" /> hello@gocoachify.com
                  </a>
                </div>
                <div>
                  <h4 className="text-background/30 text-[11px] font-semibold uppercase tracking-[0.1em] mb-3">
                    Social
                  </h4>
                  <div className="flex gap-2">
                    {socialLinks.map((link, index) => {
                      const Icon = platformIcons[link.platform] || Globe;
                      return (
                        <a key={index} href={link.url} target="_blank" rel="noopener noreferrer"
                          className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center text-background/50 hover:bg-background/15 hover:text-background transition-all"
                          aria-label={platformLabels[link.platform] || link.platform}>
                          <Icon className="w-4 h-4" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="mt-16 text-center">
            <blockquote className="text-xl text-background/40 italic max-w-xl mx-auto">
              "{content.contact_quote}"
            </blockquote>
            <p className="mt-3 text-background/60 text-sm font-medium">— {content.contact_quote_author}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
